import React, { useState } from 'react'
import { m } from 'framer-motion'
import { useTheme } from '../../../context/ThemeContext'
import { useLanguage } from '../../../context/LanguageContext'
import { ExternalLink, Play, CalendarCheck } from 'lucide-react'
import ImageModal from './ImageModal'
import VideoModal from './VideoModal'
import { GalleryContent, GalleryItem } from '../../../types/content.types'
import { getImageUrl } from '../../../utils/imageUtils'
import { trackCtaClick, trackEvent } from '../../../utils/analytics'
import { NAVER_BOOKING_URL } from '../../../constants/contact'

interface GalleryProps {
    content: GalleryContent
}

const Gallery: React.FC<GalleryProps> = ({ content }) => {
    const { theme } = useTheme()
    const { t } = useLanguage()
    const [selectedImage, setSelectedImage] = useState<string | null>(null)
    const [selectedVideo, setSelectedVideo] = useState<{ url: string; title: string } | null>(null)

    const galleryItems = content.items

    const handleItemClick = (item: GalleryItem) => {
        if (item.type === 'image') {
            setSelectedImage(getImageUrl(item.src))
            trackEvent('gallery_open', { content_type: 'image', item_name: item.alt })
        } else if (item.type === 'video' && item.videoUrl) {
            setSelectedVideo({
                url: item.videoUrl,
                title: item.alt
            })
            trackEvent('gallery_open', { content_type: 'video', item_name: item.alt })
        }
    }

    const handleCloseImageModal = () => {
        setSelectedImage(null)
    }

    const handleCloseVideoModal = () => {
        setSelectedVideo(null)
    }

    return (
        <section
            id="gallery"
            className={`restaurant-gallery theme-${theme.mode}`}
        >
            <div className="restaurant-gallery-container">
                {/* 섹션 헤더 */}
                <m.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="restaurant-gallery-header"
                >
                    <span className="restaurant-gallery-label">{content.label}</span>
                    <div className="restaurant-gallery-divider" />
                    <h2 className="restaurant-gallery-title">{content.title}</h2>
                    <p className="restaurant-gallery-description">
                        {content.description}
                    </p>
                </m.div>

                {/* 비대칭 그리드 레이아웃 */}
                <div className="restaurant-gallery-grid">
                    {galleryItems.map((item, index) => {
                        const sizeClass = `restaurant-gallery-item-${item.size}`
                        const isLastSmall = item.size === 'small' && index === galleryItems.length - 1
                        
                        return (
                            <m.div
                                key={item.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                                whileHover={{ y: -4 }}
                                className={`restaurant-gallery-item ${sizeClass} ${isLastSmall ? 'restaurant-gallery-item-small-last' : ''} ${item.type === 'video' ? 'restaurant-gallery-item-video' : ''}`}
                                onClick={() => handleItemClick(item)}
                            >
                                <div className="restaurant-gallery-image-wrapper">
                                    <img
                                        src={getImageUrl(item.thumbnail || item.src)}
                                        alt={item.alt}
                                        className="restaurant-gallery-image"
                                        loading="lazy"
                                    />
                                    <div className="restaurant-gallery-overlay" />
                                    {item.type === 'video' && (
                                        <div className="restaurant-gallery-video-badge">
                                            <Play size={32} className="restaurant-gallery-play-icon" />
                                        </div>
                                    )}
                                    <div className="restaurant-gallery-caption">
                                        <p className={`restaurant-gallery-caption-text ${
                                            item.size === 'medium' ? 'restaurant-gallery-caption-text-medium' : 
                                            item.size === 'small' ? 'restaurant-gallery-caption-text-small' : ''
                                        }`}>
                                            {item.alt}
                                        </p>
                                    </div>
                                </div>
                            </m.div>
                        )
                    })}
                </div>

                {/* 네이버 플레이스 링크 */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="restaurant-gallery-link"
                >
                    <a
                        href={content.morePhotosLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="restaurant-gallery-external-link"
                        onClick={() => trackCtaClick('more_photos', 'gallery')}
                    >
                        <ExternalLink size={18} />
                        <span>{content.morePhotos}</span>
                    </a>
                    {/* 마이크로 CTA — 비주얼로 달아오른 직후 예약 유도 */}
                    <a
                        href={NAVER_BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="restaurant-gallery-reserve-link"
                        aria-label={t('cta.reserveAria')}
                        onClick={() => trackCtaClick('reserve', 'gallery')}
                    >
                        <CalendarCheck size={18} />
                        <span>{t('cta.reserveFull')}</span>
                    </a>
                </m.div>
            </div>

            {/* 이미지 모달 */}
            <ImageModal
                isOpen={selectedImage !== null}
                imageSrc={selectedImage || ''}
                imageAlt={selectedImage ? galleryItems.find(item => getImageUrl(item.src) === selectedImage && item.type === 'image')?.alt || '' : ''}
                onClose={handleCloseImageModal}
            />

            {/* 영상 모달 */}
            <VideoModal
                isOpen={selectedVideo !== null}
                videoUrl={selectedVideo?.url || ''}
                videoTitle={selectedVideo?.title || ''}
                onClose={handleCloseVideoModal}
            />
        </section>
    )
}

export default React.memo(Gallery)
